/**
 * Account Modal Component
 * Reusable modal for creating and editing accounts
 * Task 003 - Account Management
 */

import React, { useState, useEffect, Fragment } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AccountType, ACCOUNT_TYPE_DISPLAYS, CreateAccountData, UpdateAccountData } from '../types/account';
import { MoneyInput } from './MoneyInput';
import { Listbox, Transition } from '@headlessui/react';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAccountData | UpdateAccountData) => Promise<void>;
  mode: 'create' | 'edit';
  initialData?: {
    name: string;
    type: AccountType;
    balance: number;
    billing_date?: number;
  };
}

export const AccountModal: React.FC<AccountModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  initialData,
}) => {
  const [name, setName] = useState(initialData?.name || '');
  const [type, setType] = useState<AccountType>(initialData?.type || 'checking');
  const [balance, setBalance] = useState(initialData?.balance || 0);
  const [billingDate, setBillingDate] = useState<number | undefined>(initialData?.billing_date);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { t } = useTranslation();
  const isDebtAccount = ACCOUNT_TYPE_DISPLAYS[type].is_debt;
  const requiresBillingDate = type === 'credit_card' || type === 'paylater';
  const { formatMoney } = useUserPreferences();

  // Desktop detection (lg: 1024px+)
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '');
      setType(initialData?.type || 'checking');
      setBalance(initialData?.balance || 0);
      setBillingDate(initialData?.billing_date);
      setError('');
    }
  }, [isOpen, initialData]);

  const getBalanceLabel = (): string => {
    if (isDebtAccount) {
      return t('account_modal.balance_debt');
    }
    return t('account_modal.balance_normal');
  };

  const getBalancePlaceholder = (): string => {
    if (isDebtAccount) {
      return `${t('account_modal.example')} - ${formatMoney(5000000)}`;
    }
    return `${t('account_modal.example')} ${formatMoney(1500000)}`;
  };

  const validateBalance = (balanceValue: number): boolean => {
    if (isDebtAccount) {
      return balanceValue <= 0;
    } else {
      return balanceValue >= 0;
    }
  };

  const colorMap = {
    green: {
      text: 'text-emerald-600',
      bg: 'bg-emerald-100',
      hoverText: 'text-emerald-900',
    },
    blue: {
      text: 'text-blue-600',
      bg: 'bg-blue-100',
      hoverText: 'text-blue-900',
    },
    violet: {
      text: 'text-violet-600',
      bg: 'bg-violet-100',
      hoverText: 'text-violet-900',
    },
  };

  const ACCOUNT_OPTIONS = [
    {
      groupLabel: t('account_modal.group_cash'),
      description: t('account_modal.group_cash_desc'),
      color: 'green',
      options: [
        { value: 'checking', label: `∘ ${t('account_modal.type_checking')}` },
        { value: 'saving', label: `∘ ${t('account_modal.type_saving')}` },
        { value: 'e_wallet', label: `∘ ${t('account_modal.type_ewallet')}` },
        { value: 'cash', label: `∘ ${t('account_modal.type_cash')}` },
      ],
    },
    {
      groupLabel: t('account_modal.group_credit'),
      description: t('account_modal.group_credit_desc'),
      color: 'blue',
      options: [
        { value: 'credit_card', label: `∘ ${t('account_modal.type_credit_card')}` },
        { value: 'paylater', label: `∘ ${t('account_modal.type_paylater')}` },
      ],
    },
    {
      groupLabel: t('account_modal.group_loan'),
      description: t('account_modal.group_loan_desc'),
      color: 'violet',
      options: [
        { value: 'loan', label: `∘ ${t('account_modal.type_loan')}` },
      ],
    },
  ];


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError(t('account_modal.error_name_required'));
      return;
    }

    if (!validateBalance(balance)) {
      if (isDebtAccount) {
        setError(t('account_modal.error_balance_debt'));
      } else {
        setError(t('account_modal.error_balance_normal'));
      }
      return;
    }

    // Validate billing date for credit cards and paylater
    if (requiresBillingDate) {
      if (!billingDate || billingDate < 1 || billingDate > 28) {
        setError(t('account_modal.error_billing_date'));
        return;
      }
    }

    try {
      setIsSubmitting(true);
      if (mode === 'create') {
        const data: CreateAccountData = {
          name: name.trim(),
          type,
          balance: balance,
        };
        if (requiresBillingDate && billingDate) {
          data.billing_date = billingDate;
        }
        await onSubmit(data);
      } else {
        const data: UpdateAccountData = {
          name: name.trim(),
          type,
          balance: balance,
        };
        if (requiresBillingDate && billingDate) {
          data.billing_date = billingDate;
        }
        await onSubmit(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_modal.error_save_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-md w-full"
        style={{ boxShadow: '4px 4px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4 flex items-center justify-between">
          <h2
            className="text-white uppercase"
            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
          >
            {mode === 'create' ? t('account_modal.add_title') : t('account_modal.edit_title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-[#077f5d]"
            style={{ border: '2px solid #077f5d' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Account Type */}
          <div>
            <label htmlFor="account-type" className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              {t('account_modal.account_type')}
            </label>
            <Listbox value={type} onChange={setType} disabled={isSubmitting}>
              <div className="relative">
                <Listbox.Button className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c] text-left" style={{ boxShadow: '4px 4px 0 #077f5d' }}>
                  <span className="block truncate">
                    {
                      ACCOUNT_OPTIONS.flatMap(group => group.options)
                        .find(option => option.value === type)?.label.replace(/^∘\s*/, '') || t('account_modal.select_type')
                    }
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto bg-white border-2 border-[#077f5d] py-1 text-base focus:outline-none sm:text-sm" style={{ boxShadow: '4px 4px 0 #077f5d' }}>
                    {ACCOUNT_OPTIONS.map((group) => {
                      const colors = colorMap[group.color as keyof typeof colorMap];
                      return (
                        <div key={group.groupLabel} className="px-4 py-2">
                          <h3 className={`font-semibold ${colors.text} whitespace-pre-line`}>
                            {group.groupLabel}
                          </h3>
                          <p className={`${colors.text} text-xs mb-2 whitespace-pre-line`}>
                            {group.description}
                          </p>
                          {group.options.map((option) => (
                            <Listbox.Option
                              key={option.value}
                              value={option.value}
                              className={({ active }) =>
                                `cursor-pointer select-none px-4 py-2 ${active ? `${colors.bg} ${colors.hoverText}` : 'text-gray-900'
                                }`
                              }
                            >
                              {({ selected }) => (
                                <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                  {option.label}
                                </span>
                              )}
                            </Listbox.Option>
                          ))}
                        </div>
                      );
                    })}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          {/* Account Name */}
          <div>
            <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              {t('account_modal.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
              style={{ boxShadow: '4px 4px 0 #077f5d' }}
              placeholder="BCA"
              autoFocus
            />
          </div>

          {/* Balance */}
          <div>
            <label htmlFor="account-balance" className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              {getBalanceLabel()}
            </label>
            <MoneyInput
              id="account-balance"
              value={balance}
              onValueChange={(value) => {
                // For debt accounts, automatically make the value negative
                if (isDebtAccount && value > 0) {
                  setBalance(-value);
                } else {
                  setBalance(value);
                }
              }}
              allowNegative={isDebtAccount}
              className={`w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono focus:outline-none focus:border-[#3cbe8c] ${isDebtAccount ? 'text-red-600 font-semibold' : 'text-[#1a1a1a]'
                }`}
              style={{ boxShadow: '4px 4px 0 #077f5d' }}
              placeholder={getBalancePlaceholder()}
              disabled={isSubmitting}
              useCustomKeyboard={!isDesktop}
              keyboardTitle={getBalanceLabel()}
            />
            <p className="mt-2 font-mono text-xs text-gray-600">
              {isDebtAccount
                ? t('account_modal.balance_hint_debt')
                : t('account_modal.balance_hint_normal')}
            </p>
          </div>

          {/* Billing Date (Credit Card & Paylater) */}
          {requiresBillingDate && (
            <div>
              <label htmlFor="billing-date" className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
                {t('account_modal.billing_date')} <span className="text-red-500">*</span>
              </label>
              <input
                id="billing-date"
                type="number"
                min="1"
                max="28"
                value={billingDate || ''}
                onChange={(e) => setBillingDate(e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                style={{ boxShadow: '4px 4px 0 #077f5d' }}
                placeholder="Contoh: 23"
                disabled={isSubmitting}
              />
              <p className="mt-2 font-mono text-xs text-gray-600">
                {t('account_modal.billing_date_hint')}
              </p>
            </div>
          )}

          {error && (
            <div
              className="bg-red-500 border-2 border-red-700 px-3 py-2"
              style={{ boxShadow: '3px 3px 0 #c0392b' }}
            >
              <p className="font-mono text-xs text-white">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '4px 4px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
              disabled={isSubmitting}
            >
              {t('account_modal.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold uppercase disabled:opacity-50"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '6px 6px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('account_modal.saving') : t('account_modal.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
